import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  AddOrgUserRequestInner,
  Org,
  UserRole,
  UserRolesService,
  InvitationsService,
  CreateShareInvitationRequest,
} from 'ldt-identity-service-api';
import { CellEditRequestEvent, ColDef, GridApi } from 'ag-grid-community';
import { DeleteConfirmationComponent } from 'src/app/shared/components/delete-confirmation/delete-confirmation.component';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { DarkModeService } from 'src/app/shared/dark-mode/dark-mode.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { HostnameService } from 'src/app/hostname-router-service';
import { InvitationsCreateDialogComponent } from '../invitations-create-dialog/invitations-create-dialog.component';

@Component({
  selector: 'app-team-users',
  templateUrl: './team-users.component.html',
  styleUrls: ['./team-users.component.scss'],
  providers: [DatePipe],
  standalone: false,
})
export class TeamUsersComponent implements OnInit {
  @Input() org: Org;
  @Input() canAdmin: boolean;

  // Emits the current active member count whenever the user list (re)loads, so
  // parents can compute remaining seats against the org's maxUsers cap.
  @Output() memberCountChange = new EventEmitter<number>();

  isWorkforceMode: boolean = HostnameService.isWorkforceHostname();

  /// AG -GRID --------------
  private simpleFilterParams: any = {
    filterOptions: ['contains'],
    suppressAndOrCondition: true,
  };
  defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    resizable: true,
    filterParams: this.simpleFilterParams,
    menuTabs: ['generalMenuTab', 'filterMenuTab'],
    suppressHeaderMenuButton: true,
    suppressFloatingFilterButton: true,
    suppressHeaderFilterButton: true,
  };

  columnDefs: ColDef[] = [
    {
      field: 'user.name',
      headerName: 'Name',
      sort: 'asc',
      headerTooltip: 'Name of the user',
    },
    {
      field: 'role',
      headerName: 'Role',
      maxWidth: 150,
      filterParams: { values: ['admin', 'viewer', 'editor'] },
      filter: 'agSetColumnFilter',
      headerTooltip: 'Role this user has (either admin, editor, or viewer)',
      cellRenderer: (params: any) => {
        return `
          <div class="d-flex align-items-center tw-cursor-pointer tw-gap-2">
            <span>${params.value}</span>
            <span class="tw-ml-1">✎</span>
          </div>
        `;
      },
      singleClickEdit: true,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['viewer', 'editor', 'admin'],
      },
    },
    { field: 'user.email', headerName: 'Email', headerTooltip: 'Email address of the user' },
    {
      field: 'user.lastLoginAt',
      headerName: 'Last Active (UTC)',
      maxWidth: 150,
      valueFormatter: (params: any) => {
        return this.datePipe.transform(params.value, 'yyyy-MM-dd', 'UTC') || '';
      },
      headerTooltip: 'Date user last logged in',
    },
  ];
  rowData: UserRole[];
  emails: string = '';
  rowSelection = { mode: 'multiRow', headerCheckbox: false, enableClickSelection: false };
  tooltipShowDelay = 200;

  private gridApi: GridApi;
  private orgId: string = '';
  roles: string[] = ['viewer', 'editor', 'admin'];
  role: string;

  components = {
    loadingRenderer: function (params: any) {
      if (params.value !== undefined) {
        return params.value;
      } else {
        return '<img src="https://www.ag-grid.com/example-assets/loading.gif">';
      }
    },
  };
  onGridReady(params: any) {
    this.gridApi = params.api;
    this.initView();
    this.gridApi.sizeColumnsToFit();

    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      });
    });
  }

  refreshing: boolean = true;

  constructor(
    private userRolesService: UserRolesService,
    private route: ActivatedRoute,
    private notify: NotificationService,
    public dialog: MatDialog,
    private datePipe: DatePipe,
    public darkModeService: DarkModeService,
    private authService: AuthService,
    private invService: InvitationsService
  ) {
    this.orgId = route.snapshot.paramMap.get('orgId') || '';
  }

  ngOnInit() {
    // Use org.id from Input if provided, otherwise use orgId from route
    if (this.org && this.org.id) {
      this.orgId = this.org.id;
    }
  }

  initView() {
    this.refreshOrgUsers();
  }

  refreshOrgUsers() {
    this.refreshing = true;
    this.userRolesService.getOrgUsers(this.orgId).subscribe({
      next: (r) => {
        this.rowData = r;
        this.refreshing = false;
        this.memberCountChange.emit(r.length);
      },
      error: () => {
        this.notify.error('Oops. There was an error during your request. Please try again later.');
        this.rowData = [];
        this.refreshing = false;
      },
    });
  }

  rowsSelected: boolean = false;
  onSelectionChanged(event: any) {
    var selectedRows = this.gridApi.getSelectedRows();
    this.rowsSelected = selectedRows.length !== 0;
  }

  updateUser(evt: CellEditRequestEvent) {
    switch (evt.column.getColId()) {
      case 'role':
        const req: AddOrgUserRequestInner = {
          userId: evt.data.user.id,
          role: evt.value,
        };
        this.userRolesService.updateOrgUser(this.orgId, [req]).subscribe({
          next: () => {
            this.notify.success('Role updated');
            this.refreshOrgUsers();
          },
          error: () => {
            this.notify.error('Error updating role');
          },
        });
        break;
    }
  }

  removeSelectedUsers() {
    var numToDelete = this.gridApi.getSelectedRows().length;
    var ids: any = this.gridApi.getSelectedRows().map((d: any) => d.user.id);

    const confirmDialog = this.dialog.open(DeleteConfirmationComponent, {
      data: {
        title: 'Confirm User Removal',
        message: 'Are you sure you want to remove ' + numToDelete + ' users from this Org?',
      },
    });
    confirmDialog.afterClosed().subscribe((result: boolean) => {
      if (result === true) {
        this.userRolesService.deleteOrgUsers(this.orgId, { userIds: ids }).subscribe({
          next: (response) => {
            this.gridApi.deselectAll();
            this.refreshOrgUsers();
            this.notify.success(numToDelete + ' user(s) successfully removed.');
          },
          error: () => {
            this.notify.error(
              'Oops. There was an error during your request. Please try again later.'
            );
          },
        });
      }
    });
  }

  showInviteDialog(evt?: MouseEvent) {
    evt?.stopPropagation();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '500px';

    const dialogRef = this.dialog.open(InvitationsCreateDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((data) => {
      if (data && data.emails && Array.isArray(data.emails)) {
        data.emails.forEach((email: string) => {
          const req: CreateShareInvitationRequest = {
            invitee_email: email.trim(),
            invited_by_name: this.authService.getUserValue.name,
            role: data.role,
          };

          this.invService.createShareInvitation('workforce', this.orgId, req).subscribe({
            next: (r) => {
              this.notify.success(email + ' successfully invited.');
              this.refreshOrgUsers();
            },
            error: (err) => {
              if (err.status && err.status === 409) {
                this.notify.error('User already has access to this org');
              } else {
                this.notify.error('Failed to invite ' + email + '. Please try again later.');
              }
            },
          });
        });
      }
    });
  }
}
